import React, { useMemo, useState } from "react";
import { Modal, TextArea, RadioGroup, Radio, Button, Typography, Toast } from "@douyinfe/semi-ui";
import { ManualApi, ManualAiGenerateResult } from "./api";

const { Text } = Typography;

interface Props {
  visible: boolean;
  onApply: (html: string) => void;
  onClose: () => void;
}

export function AigcModal({ visible, onApply, onClose }: Props) {
  const [description, setDescription] = useState("");
  const [style, setStyle] = useState<"steps" | "flow" | "comparison" | "general">("general");
  const [result, setResult] = useState<ManualAiGenerateResult | null>(null);
  const [loading, setLoading] = useState(false);

  const previewDoc = useMemo(() => {
    if (!result?.html) return "";
    return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <link href="/api/mis/manual/theme.css" rel="stylesheet">
  <script src="/static/manual/theme/vendor/mermaid.min.js" defer></script>
</head>
<body>
${result.html}
<script>window.addEventListener('load',function(){if(window.mermaid)mermaid.initialize({startOnLoad:true,theme:'forest'});});</script>
</body>
</html>`;
  }, [result]);

  const onGenerate = async () => {
    if (description.trim().length < 5) {
      Toast.warning("请输入更详细的描述（≥5 字）");
      return;
    }
    setLoading(true);
    try {
      const resp = await ManualApi.aiGenerate({ description: description.trim(), style });
      setResult(resp);
    } catch (e: any) {
      Toast.error("生成失败：" + (e?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const onApplyInner = () => {
    if (!result?.html) return;
    onApply(result.html);
    setDescription("");
    setResult(null);
  };

  return (
    <Modal
      title="✨ AI 生成操作手册"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>描述（您希望这个手册讲什么）:</Text>
        <TextArea
          value={description}
          onChange={setDescription}
          rows={4}
          placeholder="例如：客户列表页，主要功能：筛选/查看详情/拨打电话/导出数据"
          style={{ marginTop: 8 }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text strong>风格:</Text>
        <RadioGroup value={style} onChange={e => setStyle(e.target.value)} style={{ marginLeft: 16 }}>
          <Radio value="steps">步骤型</Radio>
          <Radio value="flow">流程型</Radio>
          <Radio value="comparison">对比型</Radio>
          <Radio value="general">综合型</Radio>
        </RadioGroup>
      </div>

      <Button theme="solid" type="primary" loading={loading} onClick={onGenerate}>✨ 生成</Button>

      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <Text strong>📝 预览</Text>
            <Text type="tertiary" size="small">
              模型: {result.model} · {result.completionTokens} tokens · {(result.latencyMs / 1000).toFixed(1)}s
            </Text>
          </div>
          <iframe
            srcDoc={previewDoc}
            style={{ width: "100%", height: 400, border: "1px solid #eee" }}
            title="AI 生成预览"
            sandbox="allow-scripts allow-same-origin"
          />
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Button onClick={onGenerate} loading={loading} style={{ marginRight: 8 }}>🔄 重新生成</Button>
            <Button theme="solid" type="primary" onClick={onApplyInner}>✅ 应用到编辑器</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
